import { useQueryWrapper } from 'apis/config/use-api-wrapper';
import type { AxiosError } from 'axios';
import { QueryKey, UseQueryOptions } from 'react-query';
import http from '../config/http-service';
import type { Paginated, ProductQuoteHistoryEntry } from './types';
import { restaurantInquiriesEndpoint } from './use-create-restaurant-inquiry-api';

// GET api/v1/cms/restaurant-inquiries/master-products/:masterProductId/quote-history —
// cross-inquiry quote timeline for one master product, every supplier (quotes name a
// master product since products was frozen; older quotes were backfilled). Pass
// excludeInquiryId = the inquiry open in the UI so its own quotes don't show up as
// "history". 404 = unknown master product.
type TData = Paginated<ProductQuoteHistoryEntry>;
type TError = AxiosError;
type Options = Omit<UseQueryOptions<TData, TError, TData, QueryKey>, 'queryKey' | 'queryFn'>;

export interface QuoteHistoryParams {
  page?: number;
  limit?: number; // backend caps at 100
  excludeInquiryId?: number;
}

export function useGetMasterProductQuoteHistoryApi(
  masterProductId: number | null | undefined,
  { page = 1, limit = 20, excludeInquiryId }: QuoteHistoryParams = {},
  options?: Options
) {
  return useQueryWrapper<TData, TError>(
    [restaurantInquiriesEndpoint, 'quote-history', masterProductId, { page, limit, excludeInquiryId }],
    async () => {
      const response = await http.get(
        `${restaurantInquiriesEndpoint}/master-products/${masterProductId}/quote-history`,
        // excludeInquiryId must never be sent as a non-numeric value (backend 400s).
        { params: { page, limit, excludeInquiryId } }
      );
      return response.data;
    },
    { enabled: masterProductId != null, ...options }
  );
}
